import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { host } from '../script/variables';
import Icon from './DashboardIcons';
import LivePulse from './LivePulse';
import '../style/SingleDashboard.css';

/* ═══════════════════════════════════════════════════════════════
   UTILITIES
═══════════════════════════════════════════════════════════════ */
const fmt    = (n) => Number(n || 0).toLocaleString('en-IN');
// Exact rupee amount — never abbreviated. Paise are shown only when they exist.
const fmtMoney = (n) => {
  const v = Number(n || 0);
  return Math.abs(v % 1) < 0.005
    ? Math.round(v).toLocaleString('en-IN')
    : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// fmtK stays for axis ticks / dense lists where space beats precision.
const fmtK   = (n) => n >= 100000 ? `${(n/100000).toFixed(1)}L` : n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(Math.round(n||0));
const fmtPct = (n) => n === null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
const pct    = (a, b) => (!b || b === 0) ? null : ((a - b) / b) * 100;

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const toIST   = (d) => new Date(d.getTime() + IST_OFFSET_MS);
const fromIST = (d) => new Date(d.getTime() - IST_OFFSET_MS);
const iso = (d) => toIST(d).toISOString().slice(0, 10);
const today = () => {
  const ist = toIST(new Date());
  ist.setUTCHours(0, 0, 0, 0);
  return fromIST(ist);
};
const addD = (d, n) => {
  const ist = toIST(d);
  ist.setUTCDate(ist.getUTCDate() + n);
  ist.setUTCHours(0, 0, 0, 0);
  return fromIST(ist);
};
const istNow = () => {
  const ist = toIST(new Date());
  return { year: ist.getUTCFullYear(), month: ist.getUTCMonth(), day: ist.getUTCDate() };
};

const PRESETS = {
  today: () => {
    const d    = iso(today());
    const prev = iso(addD(today(), -1));
    return { label: 'Today', curr: { from: d, to: d }, prev: { from: prev, to: prev } };
  },
  week: () => {
    const end   = today();
    const start = addD(end, -6);
    const pe    = addD(start, -1);
    const ps    = addD(pe, -6);
    return { label: 'This Week', curr: { from: iso(start), to: iso(end) }, prev: { from: iso(ps), to: iso(pe) } };
  },
  month: () => {
    const { year, month } = istNow();
    const csIST = new Date(Date.UTC(year, month,     1));
    const ceIST = new Date(Date.UTC(year, month + 1, 0));
    const psIST = new Date(Date.UTC(year, month - 1, 1));
    const peIST = new Date(Date.UTC(year, month,     0));
    return {
      label: 'This Month',
      curr: { from: iso(fromIST(csIST)), to: iso(fromIST(ceIST)) },
      prev: { from: iso(fromIST(psIST)), to: iso(fromIST(peIST)) },
    };
  },
};

const STATUS_CFG = [
  { key: 'Pending',   label: 'Pending',   color: '#f59e0b' },
  { key: 'Accepted',  label: 'Accepted',  color: '#3b82f6' },
  { key: 'Preparing', label: 'Preparing', color: '#6366f1' },
  { key: 'Ready',     label: 'Ready',     color: '#10b981' },
  { key: 'Completed', label: 'Completed', color: '#059669' },
  { key: 'Cancelled', label: 'Cancelled', color: '#9ca3af' },
  { key: 'Rejected', label: 'Rejected', color: '#9ca3af' },
];

/* ═══════════════════════════════════════════════════════════════
   API
═══════════════════════════════════════════════════════════════ */
const getToken = () => localStorage.getItem('partnertoken');

const fetchStats = async (cafeid, from, to) => {
  const r = await fetch(`${host}/res_stats/get_revenue_stats`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: getToken() },
    body: JSON.stringify({ restaurantId: cafeid, selectedDate: iso(today()), fromDate: from, toDate: to }),
  });
  const j = await r.json();
  if (!j.success) throw new Error(j.message || 'Stats fetch failed');
  return j;
};

const fetchProfitStats = async (cafeid, from, to) => {
  const r = await fetch(`${host}/api/expense/get_profit_stats`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: getToken() },
    body: JSON.stringify({ cafeid, startDate: from, endDate: to }),
  });
  const j = await r.json();
  if (!j.success) throw new Error(j.msg || 'Profit stats fetch failed');
  return j;
};

const fetchOrders = async (restaurantId, { fromDate = null, toDate = null } = {}) => {
  const r = await fetch(`${host}/order/fetchordersummary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: getToken() },
    body: JSON.stringify({ cafeid: restaurantId, isDate: true, fromDate, toDate }),
  });
  return r.json();
};

/* ═══════════════════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════════════════ */
const Skel = ({ w = '100%', h = 20, r = 6, style = {} }) => (
  <div style={{ width: w, height: h, borderRadius: r, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'db-shimmer 1.4s infinite', ...style }} />
);

/* Shrinks a KPI number until it fits its card, so lakh/crore figures stay
   fully readable instead of being clipped. Starts from whatever size the
   stylesheet gives the element, so the responsive breakpoints still win. */
const useFitText = (text, minPx = 12) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let lastWidth = -1;

    const fit = (force) => {
      const width = el.clientWidth;
      // Re-fit only when the card actually changed width — keeps the
      // ResizeObserver from ping-ponging off its own font-size change.
      if (!force && width === lastWidth) return;
      lastWidth = width;
      el.style.fontSize = '';
      let size = parseFloat(window.getComputedStyle(el).fontSize) || 21;
      while (size > minPx && el.scrollWidth > el.clientWidth + 0.5) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    };

    fit(true);
    const box = el.parentElement;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => fit(false));
    ro.observe(box);
    return () => ro.disconnect();
  }, [text, minPx]);

  return ref;
};

/* ═══════════════════════════════════════════════════════════════
   KPI CARD — the only unit of information density on this dashboard
═══════════════════════════════════════════════════════════════ */
const KpiCard = ({ label, icon, value, sub, delta, deltaInvert = false, tone = 'neutral', loading, live = false }) => {
  const d = delta === null || delta === undefined ? null : Number(delta);
  const isPos = deltaInvert ? d < 0 : d > 0;
  const isNeg = deltaInvert ? d > 0 : d < 0;
  const valText = loading ? '' : String(value ?? '');
  const valRef  = useFitText(valText);

  return (
    <div className={`db-kpi db-kpi-${tone}`}>
      <div className="db-kpi-top">
        <span className="db-kpi-icon"><Icon name={icon} size={16} /></span>
        <span className="db-kpi-label">{label}</span>
        {live && <span className="db-kpi-live"><LivePulse size={10} /></span>}
      </div>
      {loading ? (
        <><Skel w="65%" h={26} r={5} style={{ margin: '8px 0 6px' }} /><Skel w="45%" h={12} r={4} /></>
      ) : (
        <>
          <div className="db-kpi-val" ref={valRef} title={valText}>{value}</div>
          <div className="db-kpi-foot">
            {sub && <span className="db-kpi-sub">{sub}</span>}
            {d !== null && (
              <span className={`db-kpi-delta ${isPos ? 'dkd-pos' : isNeg ? 'dkd-neg' : 'dkd-flat'}`}>
                <Icon name={isNeg ? 'down' : 'up'} size={10} />
                {Math.abs(d).toFixed(1)}%
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   REVENUE CHART
═══════════════════════════════════════════════════════════════ */
const RevenueChart = ({ currDays, prevDays, loading }) => {
  const data = (currDays || []).map((d, i) => ({
    label: d.date?.slice(5) || `D${i+1}`,
    curr: Math.round(d.revenue || 0),
    prev: Math.round(prevDays?.[i]?.revenue || 0),
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const curr = payload.find(p => p.dataKey === 'curr')?.value || 0;
    const prev = payload.find(p => p.dataKey === 'prev')?.value || 0;
    const g = pct(curr, prev);
    return (
      <div className="db-tooltip">
        <div className="dbt-date">{label}</div>
        <div className="dbt-row"><span>This period</span><strong>₹{fmt(curr)}</strong></div>
        <div className="dbt-row dbt-row-muted"><span>Last period</span><strong>₹{fmt(prev)}</strong></div>
        {g !== null && <div className={`dbt-delta ${g > 0 ? 'pos' : g < 0 ? 'neg' : ''}`}>{fmtPct(g)}</div>}
      </div>
    );
  };

  if (loading) return <div className="db-chart-loading"><Skel w="100%" h="100%" r={8} /></div>;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="gcurr" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#24455C" stopOpacity={0.18} />
            <stop offset="95%" stopColor="#24455C" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.05)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtK} width={40} />
        <Tooltip content={<CustomTooltip />} />
        <Area type="monotone" dataKey="prev" stroke="#cbd5e1" strokeWidth={1.3} strokeDasharray="4 3" fill="transparent" dot={false} />
        <Area type="monotone" dataKey="curr" stroke="#24455C" strokeWidth={2.25} fill="url(#gcurr)" dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: '#24455C' }} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TOP ITEMS — compact, ranked list
═══════════════════════════════════════════════════════════════ */
const TopItems = ({ items, prevItems, loading }) => {
  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[1,2,3,4,5].map(i => <Skel key={i} w="100%" h={34} r={6} />)}
    </div>
  );
  if (!items?.length) return (
    <div className="db-empty-mini"><Icon name="empty" size={20} /><span>No item data for this period</span></div>
  );
  const maxRev = items[0]?.revenue || 1;

  return (
    <div className="db-items-list">
      {items.slice(0, 5).map((item, i) => {
        const prev = prevItems?.find(p => p.name === item.name);
        const g    = prev ? pct(item.revenue, prev.revenue) : null;
        const barW = Math.round((item.revenue / maxRev) * 100);
        return (
          <div key={item.name} className="db-item-row">
            <div className="db-item-rank">{i + 1}</div>
            <div className="db-item-body">
              <div className="db-item-top-row">
                <span className="db-item-name">{item.name}</span>
                <div className="db-item-right">
                  <span className="db-item-rev">₹{fmtK(item.revenue)}</span>
                  {g !== null ? (
                    <span className={`db-item-delta ${g > 0 ? 'pos' : g < 0 ? 'neg' : 'flat'}`}>
                      <Icon name={g < 0 ? 'down' : 'up'} size={9} />{Math.abs(g).toFixed(0)}%
                    </span>
                  ) : (
                    <span className="db-item-delta new"><Icon name="new" size={9} />New</span>
                  )}
                </div>
              </div>
              <div className="db-item-bar-track">
                <div className="db-item-bar-fill" style={{ width: `${barW}%` }} />
              </div>
              <div className="db-item-qty">{item.qty} sold</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ORDER STATUS STRIP — one compact segmented row, not a grid
═══════════════════════════════════════════════════════════════ */
const StatusStrip = ({ counts, total, loading }) => {
  if (loading) return <Skel w="100%" h={40} r={8} />;
  const safeTotal = total || 1;
  return (
    <div className="db-status-strip">
      <div className="db-status-bar">
        {STATUS_CFG.map(s => {
          const c = counts[s.key] || 0;
          const w = (c / safeTotal) * 100;
          return w > 0 ? <span key={s.key} style={{ width: `${w}%`, background: s.color }} title={`${s.label}: ${c}`} /> : null;
        })}
      </div>
      <div className="db-status-legend">
        {STATUS_CFG.map(s => (
          <span key={s.key} className="db-status-chip">
            <span className="db-status-dot" style={{ background: s.color }} />
            {s.label} <strong>{counts[s.key] || 0}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   DATE RANGE PICKER
═══════════════════════════════════════════════════════════════ */
const DateRangePicker = ({ value, onChange, onClose }) => {
  const [from, setFrom] = useState(value.from || iso(today()));
  const [to,   setTo]   = useState(value.to   || iso(today()));

  return (
    <div className="db-drp">
      <div className="db-drp-row">
        <label>From</label>
        <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} />
      </div>
      <div className="db-drp-row">
        <label>To</label>
        <input type="date" value={to} min={from} max={iso(today())} onChange={e => setTo(e.target.value)} />
      </div>
      <div className="db-drp-actions">
        <button className="db-drp-cancel" onClick={onClose}>Cancel</button>
        <button className="db-drp-apply" onClick={() => { onChange({ from, to }); onClose(); }}>Apply</button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════════════════════════ */
const Dashboard = ({ cafeid, socket }) => {
  const [preset,      setPreset]      = useState('today');
  const [customRange, setCustomRange] = useState(null);
  const [showDRP,     setShowDRP]     = useState(false);
  const [statsLoad,   setStatsLoad]   = useState(true);
  const [error,       setError]       = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const [currData,   setCurrData]   = useState(null);
  const [prevData,   setPrevData]   = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [prevProfit, setPrevProfit] = useState(null);
  const [profitLoad, setProfitLoad] = useState(true);

  const [allRangeOrders,  setAllRangeOrders]  = useState({});
  const [prevRangeOrders, setPrevRangeOrders] = useState({});
  const [rangeOrdersLoad, setRangeOrdersLoad] = useState(true);

  const pollRef = useRef(null);
  const drpRef  = useRef(null);

  const getRange = useCallback(() => {
    if (preset === 'custom' && customRange) {
      const diffMs   = new Date(customRange.to) - new Date(customRange.from);
      const diffD    = Math.round(diffMs / 86400000);
      const prevTo   = iso(addD(new Date(customRange.from), -1));
      const prevFrom = iso(addD(new Date(customRange.from), -(diffD + 1)));
      return { label: `${customRange.from} → ${customRange.to}`, curr: customRange, prev: { from: prevFrom, to: prevTo } };
    }
    return PRESETS[preset]?.() || PRESETS.today();
  }, [preset, customRange]);

  const loadStats = useCallback(async () => {
    setStatsLoad(true); setProfitLoad(true); setError('');
    try {
      const range = getRange();
      const [curr, prev, profit, prevProfitData] = await Promise.all([
        fetchStats(cafeid, range.curr.from, range.curr.to),
        fetchStats(cafeid, range.prev.from, range.prev.to),
        fetchProfitStats(cafeid, range.curr.from, range.curr.to).catch(() => null),
        fetchProfitStats(cafeid, range.prev.from, range.prev.to).catch(() => null),
      ]);
      setCurrData(curr); setPrevData(prev);
      setProfitData(profit); setPrevProfit(prevProfitData);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load stats');
    } finally {
      setStatsLoad(false); setProfitLoad(false);
    }
  }, [cafeid, getRange]);

  const loadRangeOrders = useCallback(async () => {
    setRangeOrdersLoad(true);
    try {
      const range = getRange();
      const [currRes, prevRes] = await Promise.all([
        fetchOrders(cafeid, { fromDate: range.curr.from, toDate: range.curr.to }),
        fetchOrders(cafeid, { fromDate: range.prev.from, toDate: range.prev.to }),
      ]);
      setAllRangeOrders(currRes  || {});
      setPrevRangeOrders(prevRes || {});
    } catch (e) {
      console.error('loadRangeOrders error:', e);
    } finally {
      setRangeOrdersLoad(false);
    }
  }, [cafeid, getRange]);

  useEffect(() => { loadStats();       }, [loadStats]);
  useEffect(() => { loadRangeOrders(); }, [loadRangeOrders]);

  useEffect(() => {
    if (!socket) return;
    socket.emit('join-room', cafeid);
    const refresh = () => { loadStats(); loadRangeOrders(); };
    socket.on('receive-message',             refresh);
    socket.on('recieve-order-status-change', refresh);
    return () => {
      socket.off('receive-message',             refresh);
      socket.off('recieve-order-status-change', refresh);
    };
  }, [socket, cafeid, loadStats, loadRangeOrders]);

  useEffect(() => {
    pollRef.current = setInterval(loadRangeOrders, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadRangeOrders]);

  useEffect(() => {
    const fn = (e) => { if (drpRef.current && !drpRef.current.contains(e.target)) setShowDRP(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  /* ── Derived values ── */
  const cDays  = currData?.periodAnalytics?.dailyComparison || [];
  const pDays  = prevData?.periodAnalytics?.dailyComparison || [];
  const cItems = currData?.salesBreakdown?.topItems         || [];
  const pItems = prevData?.salesBreakdown?.topItems         || [];
  const range  = getRange();

  const currSumm    = allRangeOrders?.summary      || {};
  const orderCounts = allRangeOrders?.statusCounts || {};
  const prevSumm    = prevRangeOrders?.summary      || {};

  const totalRevenue  = currSumm.totalRevenue  || 0;
  const totalOrders   = currSumm.totalOrders   || 0;
  const avgOrderValue = currSumm.avgOrderValue || 0;
  const totalActive   = currSumm.activeOrders  || 0;

  const totalPending = currSumm.pending?.amount || 0;
  const totalDue     = currSumm.due?.amount     || 0;
  const outstanding  = totalPending + totalDue;
  const outstandingCount = (currSumm.pending?.count || 0) + (currSumm.due?.count || 0);

  const prevTotalRevenue  = prevSumm.totalRevenue  || 0;
  const prevTotalOrders   = prevSumm.totalOrders   || 0;
  const prevAvgOrderValue = prevSumm.avgOrderValue || 0;
  const prevOutstanding   = (prevSumm.pending?.amount || 0) + (prevSumm.due?.amount || 0);

  const pWC  = profitData?.profitLoss?.withCogs || {};
  const ppWC = prevProfit?.profitLoss?.withCogs || {};

  const gstCollected     = profitData?.revenue?.gstCollected     ?? null;
  const prevGstCollected = prevProfit?.revenue?.gstCollected     ?? null;

  const statusTotal = Object.values(orderCounts).reduce((s, v) => s + (v || 0), 0);

  const rangeLabel = range.curr.from === range.curr.to ? range.curr.from : `${range.curr.from} → ${range.curr.to}`;

  return (
    <div className="db-shell">

      {/* TOPBAR */}
      <div className="db-topbar">
        <div className="db-topbar-left">
          <h1 className="db-brand">Overview</h1>
          {lastRefresh && (
            <span className="db-refresh-ts">
              <LivePulse size={10} />
              Updated {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <div className="db-topbar-right">
          <div className="db-presets">
            {[{ key: 'today', label: 'Today' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }].map(p => (
              <button key={p.key} className={`db-preset-btn ${preset === p.key ? 'dbp-on' : ''}`} onClick={() => { setPreset(p.key); setShowDRP(false); }}>
                {p.label}
              </button>
            ))}
            <div className="db-custom-wrap" ref={drpRef}>
              <button className={`db-preset-btn db-custom-btn ${preset === 'custom' ? 'dbp-on' : ''}`} onClick={() => setShowDRP(v => !v)}>
                <Icon name="calendar" size={13} />
                {preset === 'custom' && customRange ? `${customRange.from.slice(5)} → ${customRange.to.slice(5)}` : 'Custom'}
              </button>
              {showDRP && (
                <DateRangePicker
                  value={customRange || { from: iso(today()), to: iso(today()) }}
                  onChange={(r) => { setCustomRange(r); setPreset('custom'); }}
                  onClose={() => setShowDRP(false)}
                />
              )}
            </div>
          </div>
          <button className="db-refresh-btn" onClick={() => { loadStats(); loadRangeOrders(); }} title="Refresh">
            <Icon name="refresh" size={15} />
          </button>
        </div>
      </div>

      <div className="db-period-bar">
        <span className="db-period-label">{range.label}</span>
        <span className="db-period-range">{range.curr.from} → {range.curr.to}</span>
        <span className="db-period-vs">vs {range.prev.from} → {range.prev.to}</span>
      </div>

      {error && (
        <div className="db-error-bar">
          {error}
          <button onClick={() => { loadStats(); loadRangeOrders(); }}>Retry</button>
        </div>
      )}

      {/* KPI GRID — the six numbers an owner actually checks */}
      <div className="db-kpi-grid">
        <KpiCard
          icon="revenue" label="Revenue"
          value={`₹${fmtMoney(totalRevenue)}`}
          sub="for selected period"
          delta={pct(totalRevenue, prevTotalRevenue)}
          loading={rangeOrdersLoad}
        />
        <KpiCard
          icon="gst" label="GST Collected"
          value={gstCollected !== null ? `₹${fmtMoney(gstCollected)}` : '—'}
          sub={gstCollected !== null ? '@5% inclusive' : 'No expense data'}
          delta={gstCollected !== null && prevGstCollected !== null ? pct(gstCollected, prevGstCollected) : null}
          loading={profitLoad}
        />
        <KpiCard
          icon="orders" label="Orders"
          value={fmt(totalOrders)}
          sub={`${totalActive} active now`}
          delta={pct(totalOrders, prevTotalOrders)}
          loading={rangeOrdersLoad}
        />
        <KpiCard
          icon="aov" label="Avg Order Value"
          value={`₹${fmtMoney(avgOrderValue)}`}
          sub="per order"
          delta={pct(avgOrderValue, prevAvgOrderValue)}
          loading={rangeOrdersLoad}
        />
        <KpiCard
          icon="profit" label="Net Profit"
          value={profitData ? `₹${fmtMoney(pWC.netProfit || 0)}` : '—'}
          sub={profitData ? `${pWC.netMarginPct ?? '—'}% margin` : 'No expense data'}
          delta={profitData && prevProfit ? pct(pWC.netProfit, ppWC.netProfit) : null}
          tone={profitData && (pWC.netProfit || 0) < 0 ? 'loss' : 'profit'}
          loading={profitLoad}
        />
        <KpiCard
          icon="active" label="Active Orders"
          value={fmt(totalActive)}
          sub="in the kitchen / queue"
          delta={null}
          live
          loading={rangeOrdersLoad}
        />
        <KpiCard
          icon="outstanding" label="Outstanding"
          value={`₹${fmtMoney(outstanding)}`}
          sub={`${outstandingCount} order${outstandingCount !== 1 ? 's' : ''} unpaid`}
          delta={pct(outstanding, prevOutstanding)}
          deltaInvert
          tone={outstanding > 0 ? 'warn' : 'neutral'}
          loading={rangeOrdersLoad}
        />
      </div>

      {/* CHART + TOP ITEMS */}
      <div className="db-main-row">
        <div className="db-panel db-panel-chart">
          <div className="db-panel-hd">
            <h2 className="db-panel-title">Revenue Trend</h2>
            <span className="db-panel-badge">{rangeOrdersLoad ? '—' : fmtPct(pct(totalRevenue, prevTotalRevenue))}</span>
          </div>
          <div className="db-chart-area db-chart-tall">
            <RevenueChart currDays={cDays} prevDays={pDays} loading={statsLoad} />
          </div>
        </div>

        <div className="db-panel db-panel-items">
          <div className="db-panel-hd">
            <h2 className="db-panel-title">Top Items</h2>
            <span className="db-item-count">{cItems.length}</span>
          </div>
          <TopItems items={cItems} prevItems={pItems} loading={statsLoad} />
        </div>
      </div>

      {/* ORDER STATUS */}
      <div className="db-main-row db-main-row-full">
        <div className="db-panel db-panel-status">
          <div className="db-panel-hd">
            <h2 className="db-panel-title">Order Status</h2>
            <span className="db-item-count">{statusTotal} orders · {rangeLabel}</span>
          </div>
          <div className="db-panel-body">
            <StatusStrip counts={orderCounts} total={statusTotal} loading={rangeOrdersLoad} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;